import { EmberAgent as CoreEmberAgent } from '@firesim/simkit-core';
import type { Plane, FireCell } from '@firesim/simkit-core';
import type { Terrain, TerrainBase } from '@firesim/simkit-core';
import { getConstants } from '@firesim/simkit-core';
import { DarwinFrontAgent } from './darwin-front-agent';

export type DarwinEmberAgentSettings<TCodes extends TerrainBase> = {
  terrainTypes: Record<TCodes, Terrain<TCodes>>;
  protectedTerrain?: Array<TCodes>;
};

// Thin wrapper around the core EmberAgent to mirror the original agent wiring
// and leave room for future legacy-specific tweaks if needed.
export class DarwinEmberAgent<TCodes extends TerrainBase> extends CoreEmberAgent<TCodes> {
  protected burnTime = 0;

  constructor(plane: Plane<TCodes, FireCell<TCodes>>, settings: DarwinEmberAgentSettings<TCodes>) {
    super(plane, settings);
  }

  // Ensure ember-ignited fronts use DarwinFrontAgent (legacy wind behaviour)
  ignite(cell: FireCell<TCodes>): void {
    if (!this.cell) return;
    if (cell.getAgents().length === 0) {
      this.plane.addAgentToCell(new DarwinFrontAgent(this.plane, this.settings), cell);
    }
  }

  // Replicate original ember behaviour exactly (neighbour ignition condition and fade)
  run(): void {
    if (!this.cell) return;

    const constants = getConstants().agents.emberAgent;
    const startRed = 255;
    const startGreen = 255;

    const burnChance = Math.random() * constants.randomRange;

    this.cell.isBurned = true;
    this.burnTime++;

    const neighbours = this.plane.getNeighbours(this.cell.x, this.cell.y);
    neighbours.forEach((neighbour) => {
      if (!neighbour || neighbour.isBurned) return;
      if ((this.burnTime * constants.burnTimeMultiplier) + burnChance < neighbour.burnProb) {
        this.ignite(neighbour);
      }
    });

    if (this.burnTime > constants.lifetime) {
      this.plane.drawCell(this.cell.x, this.cell.y, 0, 0, 0, 255, { layer: 'fire' });
      this.plane.removeAgentFromCell(this, this.cell);
    } else {
      const red = startRed - ((startRed / constants.lifetime) * this.burnTime);
      const green = startGreen - ((startGreen / constants.lifetime) * this.burnTime * 2);
      this.plane.drawCell(this.cell.x, this.cell.y, red, green, 0, 255, { layer: 'fire' });
    }
  }
}

export default DarwinEmberAgent;
